import { useEventCallback } from '@cian/react-utils';
import { useDeviceType } from '@cian/ui-kit/responsive';
import * as React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { GEO_CHANGE_ACTIONS } from './constants';
import { trackAdvancedFiltersClick, trackClearClick, trackFilterFromChipsApply } from './tracking';
import { useLastChangedFilterType } from './utils';
import { IOnChangeParameters, Provider } from '../../../../packages/Filters';
import { NonEmptyArray } from '../../../../packages/JsonQuery';
import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { trackListingShow } from '../../../ListingMobile';
import { trackMapShow } from '../../../Map/tracking/trackMapShow';
import { fetchHybridSearchData, IFetchHybridSearchDataResult } from '../../actions/fetchHybridSearchData';
import { changeJsonQuery, fetchTags } from '../../actions/filters';
import { EListingModalMode } from '../../actions/listingModal';
import { fetchRegion } from '../../actions/region';
import { selectMlSearchSessionGuid } from '../../selectors/analytics';
import { selectIsModalOpen, selectModalMode } from '../../selectors/listingModal';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { IRegionSpecialPromo } from '../../types/specialPromos';
import { produceGeoChangeEvent } from '../../utils/events';
import { normalizeRegionId } from '../../utils/region';
import { getAvailableSpecialPromo } from '../../utils/specialPromos';
import { sendSerpFilterAppliedUxfbEvent } from '../../uxfb';
import { useApplicationContext } from '../ApplicationContext';

import type { IPublicWebV1SearchResponse } from '../../repositories/dailyrent/entities/response/PublicWebV1SearchResponse';

interface IFiltersContainerProps {
  children: React.ReactNode;
}

export const FiltersContainer: React.FC<IFiltersContainerProps> = ({ children }) => {
  const { microfrontendsAPI, config } = useApplicationContext();
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const store = useStore<IApplicationState>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const specialPromos = useSelector<IApplicationState, IRegionSpecialPromo[]>(state => state.specialPromos);
  const queryString = useSelector<IApplicationState, string>(state => state.queryString);
  const [isAwaitingTracking, setIsAwaitingTracking] = React.useState<boolean>(false);

  // Храним promise последнего apply, чтобы в useEffect дождаться
  // именно того /web/v1/map или /web/v1/search, который был запущен этим apply.
  // Без этого effect не сможет связать newpage с конкретным запросом и может
  // отправить событие со старым mlSearchSessionGuid.
  const fetchHybridSearchDataPromiseRef = React.useRef<Promise<IFetchHybridSearchDataResult | undefined> | null>(null);
  const listingOffersPromiseRef = React.useRef<Promise<IPublicWebV1SearchResponse | undefined> | null>(null);
  const isAwaitingGeoUpdate = React.useRef<boolean>(false);
  const specialPromo: IRegionSpecialPromo | undefined = getAvailableSpecialPromo(specialPromos, jsonQuery);
  const isListingModalOpen = useSelector(selectIsModalOpen);
  const listingModalMode = useSelector(selectModalMode);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);

  const { lastChangedFilterType, setLastAppliedModifiers } = useLastChangedFilterType();

  const experimentalFeatures = React.useMemo(() => ({}), []);

  const handleChange = useEventCallback((parameters: IOnChangeParameters) => {
    const nextJsonQuery = dispatch(changeJsonQuery({ ...parameters, prevJsonQuery: jsonQuery }));

    setLastAppliedModifiers(parameters.appliedModifiers);

    if (parameters.appliedModifiers.some(modifier => GEO_CHANGE_ACTIONS.includes(modifier.action))) {
      isAwaitingGeoUpdate.current = true;
      dispatch(fetchTags());
      dispatch(fetchRegion());

      if (config.get<boolean>('header.asMicrofrontend')) {
        const setRegionModifier = parameters.appliedModifiers.find(modifier => modifier.action === 'setRegion');

        if (setRegionModifier !== undefined && microfrontendsAPI?.header) {
          const regionId = normalizeRegionId(setRegionModifier.arguments[0] as NonEmptyArray<number> | null);

          if (regionId) {
            microfrontendsAPI.header.setRegionId({ regionId });
          }
        }
      } else {
        produceGeoChangeEvent(parameters.appliedModifiers);
      }
    }

    return nextJsonQuery;
  });

  const handleApply = useEventCallback(() => {
    dispatch(
      sendSerpFilterAppliedUxfbEvent({
        jsonQuery: store.getState().filters.jsonQuery,
        deviceType,
      }),
    );
    dispatch(fetchTags());
    fetchHybridSearchDataPromiseRef.current = dispatch(
      fetchHybridSearchData({ updateBounds: isAwaitingGeoUpdate.current, deviceType }),
    );
    listingOffersPromiseRef.current = fetchHybridSearchDataPromiseRef.current.then(response => response?.listingOffers);
    isAwaitingGeoUpdate.current = false;
  });

  const handleApplyClick = React.useCallback(() => {
    setIsAwaitingTracking(true);
  }, []);

  React.useEffect(() => {
    if (!isAwaitingTracking) {
      return;
    }

    trackFilterFromChipsApply({
      queryString,
      jsonQuery,
      pageType: isListingModalOpen ? 'Listing' : 'Map',
      filterType: lastChangedFilterType,
    });

    const isListingFullScreen =
      isListingModalOpen && listingModalMode === EListingModalMode.FULL_SCREEN && deviceType === 'phone';

    if (isListingFullScreen) {
      // Листинг открыт на весь экран – трекаем Listing newpage.
      // handleApply уже запустил cascade, дожидаемся listing-части.
      listingOffersPromiseRef.current?.then(res => {
        if (res) {
          trackListingShow('all_objects_on_map')(res);
        }
      });
    } else {
      const currentFetchHybridSearchDataPromise = fetchHybridSearchDataPromiseRef.current;

      // трекаем событие newpage карты только после успешного /web/v1/map
      currentFetchHybridSearchDataPromise?.then(response => {
        if (!response?.mapData) {
          return;
        }

        trackMapShow({
          jsonQuery,
          offersCount: response.mapData.offersCount,
          mlSearchSessionGuid: response.mapData.mlSearchSessionGuid ?? mlSearchSessionGuid,
        });
      });
    }

    setIsAwaitingTracking(false);
  }, [
    deviceType,
    isAwaitingTracking,
    isListingModalOpen,
    jsonQuery,
    lastChangedFilterType,
    listingModalMode,
    mlSearchSessionGuid,
    queryString,
  ]);

  return (
    <Provider
      jsonQuery={jsonQuery}
      specialPromo={specialPromo}
      onChange={handleChange}
      onApply={handleApply}
      onClear={trackClearClick}
      onApplyClick={handleApplyClick}
      onAdvancedFiltersOpen={trackAdvancedFiltersClick}
      features={experimentalFeatures}
    >
      {children}
    </Provider>
  );
};
